import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useToast } from "@/components/hooks/use-toast";
import BigPockeetIcon from "@/assets/icons/BigPockeetIcon";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { BASE_URL } from "@/api/apiUrl";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { mobileOtpVerfication } from "@/api/apiFunction";
import { useWizard } from "react-use-wizard";

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: "Your one-time password must be 6 characters.",
  }),
});

const resendOtpApiCall = async (resendData: {
  mobile: string;
  phoneCode: string;
  reson: string;
}) => {
  const response = await axios.post(`${BASE_URL}/auth/resend-otp`, resendData);
  return response.data;
};

interface MarathonOtpVerificationProps {
  mobileNumber: string;
  setUserInfo: (userInfo: any) => void;
  userInfo: any;
}

const MarathonOtpVerification: React.FC<MarathonOtpVerificationProps> = ({
  mobileNumber,
  setUserInfo,
}) => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [timer, setTimer] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { nextStep } = useWizard();
  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => clearInterval(intervalId); // Clean up the interval on unmount
    }
  }, [timer]);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: "",
    },
  });

  // submit otp
  const { mutate } = useMutation({
    mutationFn: mobileOtpVerfication,
    onSuccess: (data) => {
      toast({
        title: "OTP Verified Successfully",
      });
      setUserInfo(data);
      nextStep();
    },
    onError: (error) => {
      console.error("Error submitting form:", error);
      toast({
        title: "OTP Verification failed!",
        description: "Clicked on resend OTP to verify OTP",
      });
    },
  });

  // for resend otp
  const { mutate: resendOtp } = useMutation({
    mutationFn: resendOtpApiCall,
    onSuccess: () => {
      setTimer(60); // Reset timer to 5 minutes on successful resend
    },
    onError: (error) => {
      console.error("Error resending OTP:", error);
      toast({
        title: "Error",
        description: "Failed to resend OTP. Please try again.",
        // status: "error",
      });
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let phoneCode = searchParams.get("phoneCode") || "+91";
    if (!phoneCode.startsWith("+")) {
      phoneCode = "+" + phoneCode.trim();
    }

    const mobileOtpData = {
      otp: data.pin,
      mobile: mobileNumber,
      phoneCode,
      otpReson: "USER_VERIFICATION",
    };
    mutate(mobileOtpData);
    // console.log(mobileOtpData);
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  return (
    <div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-12 flex flex-col items-center space-y-6"
        >
          <h1 className="text-3xl font-semibold">Lets Verify And Connect</h1>
          <p className="text-[#636363]">OTP Sent on +91 {mobileNumber}</p>
          <div className="relative  ">
            <BigPockeetIcon />
            <div className="absolute top-2 left-20 w-6 h-6 bg-gradient-to-r from-[#4BD5D6] to-[#009FA6] rounded-full" />
          </div>

          <FormField
            control={form.control}
            name="pin"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#636363]">
                  Enter The 6-digit Verification Code
                </FormLabel>
                <FormControl>
                  <InputOTP autoFocus={true} maxLength={6} {...field}>
                    <InputOTPGroup className="gap-6">
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <Button
                  className="text-xs font-semibold rounded-lg bg-gradient-to-r from-[#4BD5D6] to-[#009FA6] flex gap-3 w-full"
                  type="submit"
                >
                  Verify
                </Button>
                <FormDescription>
                  <div className=" text-sm">
                    <p className=" ">Didn't receive an OTP?</p>
                    <p className=" font-medium">
                      {" "}
                      Resend a{" "}
                      {timer > 0 ? (
                        <span> OTP in {timer}sec</span>
                      ) : (
                        <span
                          className="hover:underline  cursor-pointer"
                          onClick={() => {
                            let phoneCode =
                              searchParams.get("phoneCode") || "+91";
                            if (!phoneCode.startsWith("+")) {
                              phoneCode = "+" + phoneCode.trim();
                            }

                            resendOtp({
                              mobile: mobileNumber,
                              phoneCode: phoneCode, // Send the formatted phoneCode here
                              reson: "USER_VERIFICATION",
                            });
                            toast({
                              title: "OTP Sent Successfully",
                            });
                          }}
                        >
                          New code
                        </span>
                      )}
                    </p>
                  </div>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
};

export default MarathonOtpVerification;
